'use client';

import { ReactNode, createContext, useContext, useEffect, useRef } from 'react';
import {
  GameDataRegistry,
  useGameItemData,
  useGlobalGameData,
} from '~/hooks/useGlobalGameData';
import type { IGame } from '~/types/api';

// Create context with the proper type
const GameDataContext = createContext<GameDataRegistry | null>(null);

// Provider component that will initialize the registry
export function GameDataProvider({ children }: { children: ReactNode }) {
  // Directly use the hook inside the provider component
  const gameDataRegistry = useGlobalGameData();

  return (
    <GameDataContext.Provider value={gameDataRegistry}>
      {children}
    </GameDataContext.Provider>
  );
}

// Hook to use the game data context
export function useGameData() {
  const context = useContext(GameDataContext);

  if (!context) {
    throw new Error('useGameData must be used within a GameDataProvider');
  }

  return context;
}

// Type declaration for global cache
declare global {
  var __gameRegistryCache:
    | Map<
        string,
        {
          registrationPromise: Promise<void> | null;
          isRegistered: boolean;
          componentCount: number;
          lastAccess: number;
        }
      >
    | undefined;
  var __fallbackCache: Map<string, any> | undefined;
}

// Global memoization cache to prevent duplicate registrations for the same game
// Use globalThis to persist across Fast Refresh in development
const getGameRegistryCache = () => {
  if (typeof globalThis !== 'undefined') {
    if (!globalThis.__gameRegistryCache) {
      globalThis.__gameRegistryCache = new Map<
        string,
        {
          registrationPromise: Promise<void> | null;
          isRegistered: boolean;
          componentCount: number;
          lastAccess: number;
        }
      >();
    }
    return globalThis.__gameRegistryCache;
  }

  // Fallback for non-browser environments
  if (!globalThis.__fallbackCache) {
    globalThis.__fallbackCache = new Map();
  }
  return globalThis.__fallbackCache;
};

// Clean up stale cache entries every 30 seconds
if (typeof window !== 'undefined') {
  setInterval(() => {
    const cache = getGameRegistryCache();
    const now = Date.now();
    const STALE_TIME = 60000; // 1 minute

    for (const [gameId, entry] of cache.entries()) {
      if (now - entry.lastAccess > STALE_TIME && entry.componentCount <= 0) {
        // console.log(`🗑️ Auto-cleaning stale cache entry for game ${gameId}`);
        cache.delete(gameId);
      }
    }
  }, 30000);
}

/**
 * Hook to get game-specific data with automatic registration
 * Now uses JSON file polling for real-time updates with global memoization
 * to prevent duplicate registrations across multiple components
 */
export function useGameRegistryData(game: IGame) {
  const registry = useGameData();
  const gameId = game.gameId.toString();
  const mountedRef = useRef<boolean>(true);

  // Memoized registration logic - only register once per game ID globally
  useEffect(() => {
    mountedRef.current = true;

    // Get or create cache entry for this game
    const cache = getGameRegistryCache();
    let cacheEntry = cache.get(gameId);

    if (!cacheEntry) {
      // Create new cache entry
      cacheEntry = {
        registrationPromise: null,
        isRegistered: false,
        componentCount: 0,
        lastAccess: Date.now(),
      };
      cache.set(gameId, cacheEntry);
    }

    // Update last access and increment component count
    cacheEntry.lastAccess = Date.now();
    cacheEntry.componentCount++;

    // console.log(
    //   `📊 Game ${gameId}: ${cacheEntry.componentCount} component(s) using this game`,
    // );

    // Only register if not already registered or in progress
    if (!cacheEntry.isRegistered && !cacheEntry.registrationPromise) {
      // console.log(`🔄 First registration for game ${gameId}`);

      // Create registration promise
      cacheEntry.registrationPromise = (async () => {
        try {
          await registry.registerGame(game);
          const entry = cache.get(gameId);
          if (entry) {
            entry.isRegistered = true;
            entry.registrationPromise = null;
            entry.lastAccess = Date.now();
          }
          // console.log(`✅ Game ${gameId} successfully registered`);
        } catch (error) {
          console.error(`❌ Error registering game ${gameId}:`, error);
          const entry = cache.get(gameId);
          if (entry) {
            entry.registrationPromise = null;
          }
        }
      })();
    } else if (cacheEntry.isRegistered) {
      // console.log(`♻️ Game ${gameId} already registered, reusing registration`);
    } else if (cacheEntry.registrationPromise) {
      // console.log(`⏳ Game ${gameId} registration in progress, waiting...`);
    }

    // Cleanup function for this component instance
    return () => {
      if (!mountedRef.current) return;

      const cache = getGameRegistryCache();
      const entry = cache.get(gameId);
      if (entry) {
        entry.componentCount--;
        entry.lastAccess = Date.now();
        // console.log(
        //   `📉 Game ${gameId}: ${entry.componentCount} component(s) remaining`,
        // );

        // Only unregister when no components are using this game anymore
        if (entry.componentCount <= 0 && entry.isRegistered) {
          // console.log(
          //   `🧹 Last component unmounted, unregistering game ${gameId}`,
          // );
          registry.unregisterGame(gameId);

          // Clean up cache entry after a delay to prevent rapid re-registrations
          setTimeout(() => {
            const currentEntry = cache.get(gameId);
            if (currentEntry && currentEntry.componentCount <= 0) {
              // console.log(`🗑️ Cleaning up cache for game ${gameId}`);
              cache.delete(gameId);
            }
          }, 5000);
        }
      }
    };
  }, [registry, gameId, game]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Use the updated useGameItemData hook for polling
  return useGameItemData(game);
}
